import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { API_PATH } from 'src/environments/environment';

interface FileViewerData {
  fileUrl?: string;
  fileName?: string;
  shareText?: string;
  onlyLink?: boolean;
  showShare?: boolean;
  pixBrCodes?: string[];
  showInitialLoading?: boolean;
}

@Component({
  selector: 'app-file-viewer-dialog',
  templateUrl: './file-viewer-dialog.component.html',
})
export class FileViewerDialogComponent {
  public fileUrl?: string;
  public fileName?: string;
  public shareText?: string;
  public isDownloading = false;
  private originalUrl?: string;
  public onlyLink?: boolean;
  public showShareButton = false;
  public pixBrCodes?: string[];
  public copiedIndex = -1;
  public isViewerLoading = false;

  constructor(
    public dialogRef: MatDialogRef<FileViewerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: string | FileViewerData,
    private toastService: ToastService
  ) {
    if (!data) return;
    if (typeof data === 'string') {
      this.originalUrl = data;
      this.fileUrl = `${API_PATH}alfasupay-bill?apiUrl=${encodeURIComponent(data)}`;
      this.showShareButton = true;
    } else {
      this.onlyLink = data.onlyLink;
      this.isViewerLoading = !!data.showInitialLoading;
      this.showShareButton = data.showShare ?? !data.onlyLink;
      this.pixBrCodes = data.pixBrCodes?.filter(c => !!c);
      if (data.onlyLink) {
        this.originalUrl = data.fileUrl;
        this.fileUrl = data.fileUrl;
        this.fileName = data.fileName;
        this.shareText = data.shareText;
      } else {
        this.originalUrl = data.fileUrl;
        this.fileUrl = `${API_PATH}alfasupay-bill?apiUrl=${encodeURIComponent(data.fileUrl || '')}`;
        this.fileName = data.fileName;
        this.shareText = data.shareText;
      }
    }
  }

  onViewerLoaded(): void {
    this.isViewerLoading = false;
  }

  copyPixCode(index: number): void {
    const code = this.pixBrCodes?.[index];
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      this.copiedIndex = index;
      setTimeout(() => { if (this.copiedIndex === index) this.copiedIndex = -1; }, 2500);
    }).catch(() => {
      const el = document.createElement('textarea');
      el.value = code;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.copiedIndex = index;
      setTimeout(() => { if (this.copiedIndex === index) this.copiedIndex = -1; }, 2500);
    });
  }

  async downloadFile(): Promise<void> {
    if (!this.originalUrl || this.isDownloading) return;

    this.isDownloading = true;

    try {
      if (this.originalUrl.startsWith('data:') || this.originalUrl.startsWith('blob:')) {
        const anchor = document.createElement('a');
        anchor.href = this.originalUrl;
        anchor.download = this.fileName || 'download';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        this.isDownloading = false;
        return;
      }

      // Para URLs externas, usar o backend para gerar PDF
      const backendUrl = `${API_PATH}pdf/gerar?url=${encodeURIComponent(this.originalUrl)}`;

      // Fazer requisição para o backend
      const response = await fetch(backendUrl);

      if (!response.ok) {
        throw new Error(`Erro do servidor: ${response.status}`);
      }

      // Obter o blob do PDF
      const blob = await response.blob();

      // Criar URL temporária para o blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Criar link de download
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = this.fileName || 'documento.pdf';

      // Adicionar ao DOM temporariamente e clicar
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      // Limpar URL temporária
      window.URL.revokeObjectURL(blobUrl);

      this.toastService.showSuccess('PDF baixado com sucesso!');

    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      this.toastService.showError('Erro ao baixar PDF. Tente novamente.');
    } finally {
      this.isDownloading = false;
    }
  }

  async shareFile(): Promise<void> {
    if (!this.originalUrl && !this.shareText) return;
    if (this.originalUrl?.startsWith('blob:')) {
      try {
        const resp = await fetch(this.originalUrl);
        const blob = await resp.blob();
        const file = new File([blob], this.fileName || 'documento.pdf', { type: 'application/pdf' });
        if ((navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
          await (navigator as any).share({ files: [file], title: this.fileName });
          return;
        }
      } catch {}
      this.downloadFile();
      return;
    }
    this.shareWhatsApp();
  }

  shareWhatsApp(): void {
    if (!this.originalUrl && !this.shareText) return;
    const textParts = [];
    if (this.shareText) textParts.push(this.shareText);
    if (this.originalUrl) textParts.push(this.originalUrl);
    const text = encodeURIComponent(textParts.join(' '));
    const waUrl = `https://wa.me/?text=${text}`;
    window.open(waUrl, '_blank');
  }
}
